import { WebAuthnError } from '@simplewebauthn/browser';

export enum PasskeyAuthenticateErrorType {
  USER_CANCELLED = 'USER_CANCELLED',
  GENERAL = 'GENERAL',
}

export enum PasskeyRegisterErrorType {
  DEVICE_NOT_SUPPORTED = 'DEVICE_NOT_SUPPORTED',
  ALREADY_REGISTERED = 'ALREADY_REGISTERED',
  SYSTEM_ERROR = 'SYSTEM_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  TRANSIENT_ERROR = 'TRANSIENT_ERROR',
  VERIFICATION_FAILED = 'VERIFICATION_FAILED',
  USER_CANCELLED = 'USER_CANCELLED',
  GENERAL = 'GENERAL',
}

export type PasskeyRegisterErrorState = { errorType: PasskeyRegisterErrorType; canRetry: boolean };

const isAxiosError = (error: unknown): error is { response?: { status: number } } => {
  return !!(error && typeof error === 'object' && 'response' in error);
};

const isNetworkError = (error: unknown): error is { request: unknown } => {
  return !!(error && typeof error === 'object' && 'request' in error && !('response' in error));
};

/**
 * WebAuthnError categorization based on this util function frontend/node_modules/@simplewebauthn/browser/esm/helpers/identifyRegistrationError.js
 * Should look over whenever @simplewebauthn/browser gets updated
 */
const resolveWebAuthnError = (error: WebAuthnError): PasskeyRegisterErrorState => {
  switch (error.code) {
    // Most likely user cancelled or some temporary issue with the device
    case 'ERROR_CEREMONY_ABORTED':
    case 'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY':
      return {
        errorType: PasskeyRegisterErrorType.USER_CANCELLED,
        canRetry: true,
      };

    // Device timeout/internal failure - may be temporary
    case 'ERROR_AUTHENTICATOR_GENERAL_ERROR':
      return {
        errorType: PasskeyRegisterErrorType.TRANSIENT_ERROR,
        canRetry: true,
      };

    // User verification failed - can retry
    case 'ERROR_AUTO_REGISTER_USER_VERIFICATION_FAILURE':
      return {
        errorType: PasskeyRegisterErrorType.VERIFICATION_FAILED,
        canRetry: true,
      };

    // Device can't store credentials locally
    // No biometric/PIN capability
    // Incompatible cryptographic algorithms
    case 'ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT':
    case 'ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT':
    case 'ERROR_AUTHENTICATOR_NO_SUPPORTED_PUBKEYCREDPARAMS_ALG':
      return {
        errorType: PasskeyRegisterErrorType.DEVICE_NOT_SUPPORTED,
        canRetry: false,
      };

    // Credential already registered
    case 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED':
      return {
        errorType: PasskeyRegisterErrorType.ALREADY_REGISTERED,
        canRetry: false,
      };

    // Invalid hostname (e.g., IP address)
    // RP ID doesn't match domain
    // User ID validation failed (server issue)
    // Invalid algorithm configuration (server issue)
    // Most likely hints at an issue with sso-api or sso-ui
    case 'ERROR_INVALID_DOMAIN':
    case 'ERROR_INVALID_RP_ID':
    case 'ERROR_INVALID_USER_ID_LENGTH':
    case 'ERROR_MALFORMED_PUBKEYCREDPARAMS':
      return {
        errorType: PasskeyRegisterErrorType.SYSTEM_ERROR,
        canRetry: false,
      };

    default:
      return {
        errorType: PasskeyRegisterErrorType.GENERAL,
        canRetry: false,
      };
  }
};

const resolveAxiosError = (error: { response?: { status: number } }): PasskeyRegisterErrorState => {
  const status = error.response?.status;
  if (status && status >= 500) {
    return { errorType: PasskeyRegisterErrorType.NETWORK_ERROR, canRetry: true };
  } else if (status && status >= 400) {
    return { errorType: PasskeyRegisterErrorType.SYSTEM_ERROR, canRetry: false };
  }
  return { errorType: PasskeyRegisterErrorType.GENERAL, canRetry: false };
};

const resolveNetworkError = (): PasskeyRegisterErrorState => {
  return { errorType: PasskeyRegisterErrorType.NETWORK_ERROR, canRetry: true };
};

/**
 * Categorizes passkey registration errors into semantic error types and determines retry eligibility.
 */
export const resolvePasskeyRegisterErrorState = (error: unknown): PasskeyRegisterErrorState => {
  if (error instanceof WebAuthnError) {
    return resolveWebAuthnError(error);
  }

  if (isAxiosError(error)) {
    return resolveAxiosError(error);
  }

  if (isNetworkError(error)) {
    return resolveNetworkError();
  }

  return { errorType: PasskeyRegisterErrorType.GENERAL, canRetry: false };
};
